'use client'

import type { LucideIcon } from 'lucide-react'

interface LinkBase {
  id?: number | undefined
  name: string
  link: string
  baseUrl?: string
}

export interface LinkPrimitive extends LinkBase {
  icon?: LucideIcon
  child?: LinkBase[] | undefined
  position?: 'top' | 'middle' | 'bottom'
}

export interface Links extends LinkBase {
  icon?: LucideIcon
  isCurrent: boolean
  child?: (LinkBase & { isCurrent: boolean })[] | undefined
  position?: 'top' | 'middle' | 'bottom'
}

export interface UseCurrentLinksProps {
  pathname: string
  links: LinkPrimitive[]
  urlLinksIncludeOrgId: boolean
}

/**
 * Maps special pathname patterns to their corresponding child link paths.
 * Used to handle cases where longer paths should match shorter child links.
 */
function normalizePathnameForMatching(pathname: string, childLink: string): boolean {
  // Special case: /paket-tagihan/buat-transaksi-baru/transaksi-baru/akun-keluarga -> /transaksi-akun-keluarga
  // Special case: /paket-tagihan/buat-transaksi-kursi-tambahan/transaksi-kursi-tambahan/akun-keluarga -> /transaksi-akun-keluarga
  if (childLink === '/transaksi-akun-keluarga') {
    return (
      pathname.includes('/transaksi-akun-keluarga') ||
      pathname.includes('/paket-tagihan/buat-transaksi-baru/transaksi-baru/akun-keluarga') ||
      pathname.includes('/paket-tagihan/buat-transaksi-kursi-tambahan/transaksi-kursi-tambahan/akun-keluarga')
    )
  }

  // Special case: /paket-tagihan/buat-transaksi-baru/transaksi-baru/akun-sekolah -> /transaksi-akun-sekolah
  // Special case: /paket-tagihan/buat-transaksi-kursi-tambahan/transaksi-kursi-tambahan/akun-sekolah -> /transaksi-akun-sekolah
  if (childLink === '/transaksi-akun-sekolah') {
    return (
      pathname.includes('/transaksi-akun-sekolah') ||
      pathname.includes('/paket-tagihan/buat-transaksi-baru/transaksi-baru/akun-sekolah') ||
      pathname.includes('/paket-tagihan/buat-transaksi-kursi-tambahan/transaksi-kursi-tambahan/akun-sekolah')
    )
  }

  // Prevent /akun-keluarga and /akun-sekolah from matching the longer transaksi paths
  if (childLink === '/akun-keluarga') {
    return (
      pathname.includes('/akun-keluarga') &&
      !pathname.includes('/paket-tagihan/buat-transaksi-baru/transaksi-baru/akun-keluarga') &&
      !pathname.includes('/paket-tagihan/buat-transaksi-kursi-tambahan/transaksi-kursi-tambahan/akun-keluarga')
    )
  }

  if (childLink === '/akun-sekolah') {
    return (
      pathname.includes('/akun-sekolah') &&
      !pathname.includes('/paket-tagihan/buat-transaksi-baru/transaksi-baru/akun-sekolah') &&
      !pathname.includes('/paket-tagihan/buat-transaksi-kursi-tambahan/transaksi-kursi-tambahan/akun-sekolah')
    )
  }

  // Default: use standard includes check
  return pathname.includes(childLink)
}

/**
 * Transforms link primitives into links with current state information.
 * Framework-agnostic - accepts pathname as a prop instead of using usePathname().
 *
 * @example Next.js App Router
 *
 * 'use client'
 * import { usePathname } from 'next/navigation'
 * import { useCurrentLinks } from '@ui/web/sidebar'
 *
 * function MyComponent() {
 *   const pathname = usePathname()
 *   const links = useCurrentLinks({
 *     pathname,
 *     links: [...],
 *     urlLinksIncludeOrgId: false
 *   })
 *   return <Sidebar links={links} {...otherProps} />
 * }
 *
 *
 * @example TanStack Start / Router
 *
 * import { useLocation } from '@tanstack/react-router'
 * import { useCurrentLinks } from '@ui/web/sidebar'
 *
 * function MyComponent() {
 *   const location = useLocation()
 *   const links = useCurrentLinks({
 *     pathname: location.pathname,
 *     links: [...],
 *     urlLinksIncludeOrgId: false
 *   })
 *   return <Sidebar links={links} {...otherProps} />
 * }
 *
 */
export function useCurrentLinks({ pathname, links, urlLinksIncludeOrgId }: UseCurrentLinksProps): Links[] {
  const isCurrentOrganization = pathname.split('/').find((v) => v.includes('org_'))

  const data = links.map((v) => {
    const isCurrent = pathname.includes(v.link)

    let child: undefined | { name: string; link: string; isCurrent: boolean }[] = undefined

    if (v.child) {
      const childs: { name: string; link: string; isCurrent: boolean }[] = []

      v.child.forEach((y) => {
        const isChildCurrent =
          normalizePathnameForMatching(pathname, y.link) && (v.baseUrl ? pathname.includes(v.baseUrl) : true)

        childs.push({
          name: y.name,
          link: y.link,
          isCurrent: isChildCurrent,
        })
      })

      child = childs
    }

    return {
      id: v.id,
      name: v.name,
      link: isCurrentOrganization && urlLinksIncludeOrgId ? `/${isCurrentOrganization}/${v.link}` : v.link,
      icon: v.icon,
      isCurrent,
      child,
      position: v.position,
    }
  })

  return data
}
